import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export interface ConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
  consentDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly STORAGE_KEY = 'cookie-consent';
  private readonly CONSENT_VERSION = '1.0';
  
  private consentSubject = new BehaviorSubject<ConsentState>({
    hasConsented: false,
    preferences: {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    },
    consentDate: ''
  });

  consent$ = this.consentSubject.asObservable();

  constructor() {
    this.loadConsentFromStorage();
  }

  /**
   * Check if user has given consent
   */
  hasConsented(): boolean {
    return this.consentSubject.value.hasConsented;
  }

  /**
   * Get current consent preferences
   */
  getPreferences(): CookiePreferences {
    return this.consentSubject.value.preferences;
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    this.log('Accept all cookies');
    this.saveConsent(preferences);
    this.loadScripts(preferences);
  }

  /**
   * Accept only necessary cookies
   */
  acceptNecessaryOnly(): void {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    this.log('Accept necessary cookies only');
    this.saveConsent(preferences);
  }

  /**
   * Save custom preferences
   */
  saveCustomPreferences(preferences: CookiePreferences): void {
    // Necessary cookies are always required
    preferences.necessary = true;
    
    this.log('Save custom preferences', preferences);
    this.saveConsent(preferences);
    this.loadScripts(preferences);
  }

  /**
   * Reset consent (for testing or user request)
   */
  resetConsent(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.consentSubject.next({
      hasConsented: false,
      preferences: {
        necessary: true,
        analytics: false,
        marketing: false,
        functional: false
      },
      consentDate: ''
    });
  }

  /**
   * Check if specific cookie type is allowed
   */
  isAllowed(cookieType: keyof CookiePreferences): boolean {
    return this.consentSubject.value.preferences[cookieType];
  }

  /**
   * Get debug information for development and testing
   */
  getDebugInfo(): any {
    return {
      hasConsented: this.hasConsented(),
      preferences: this.getPreferences(),
      consentDate: this.consentSubject.value.consentDate,
      version: this.CONSENT_VERSION,
      scriptsLoaded: {
        analytics: this.isScriptLoaded('google-analytics')
      },
      storageKey: this.STORAGE_KEY
    };
  }

  private saveConsent(preferences: CookiePreferences): void {
    const consentState: ConsentState = {
      hasConsented: true,
      preferences,
      consentDate: new Date().toISOString()
    };

    const storageData = {
      ...consentState,
      version: this.CONSENT_VERSION
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storageData));
      this.consentSubject.next(consentState);
      this.log('Consent saved', preferences);
    } catch (error) {
      console.error('Failed to save cookie consent:', error);
    }
  }

  private loadConsentFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data = JSON.parse(stored);
      
      // Check if consent version matches (for future updates)
      if (data.version !== this.CONSENT_VERSION) {
        this.resetConsent();
        return;
      }

      const consentState: ConsentState = {
        hasConsented: data.hasConsented,
        preferences: data.preferences,
        consentDate: data.consentDate
      };

      this.consentSubject.next(consentState);

      // Load scripts if user has already consented
      if (consentState.hasConsented) {
        this.loadScripts(consentState.preferences);
      }
    } catch (error) {
      console.warn('Failed to load cookie consent from storage:', error);
      this.resetConsent();
    }
  }

  private loadScripts(preferences: CookiePreferences): void {
    // Load Google Analytics if analytics is enabled
    if (preferences.analytics) {
      this.loadGoogleAnalytics();
    }

    // Load marketing scripts if marketing is enabled
    if (preferences.marketing) {
      this.loadMarketingScripts();
    }

    // Load functional scripts if functional is enabled
    if (preferences.functional) {
      this.loadFunctionalScripts();
    }
  }

  private loadGoogleAnalytics(): void {
    // Google Analytics 4 implementation
    const GA_MEASUREMENT_ID = environment.googleAnalyticsId;
    
    if (this.isScriptLoaded('google-analytics')) return;

    // Load gtag script
    const script = document.createElement('script');
    script.id = 'google-analytics';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    script.onload = () => {
      console.log('Google Analytics loaded successfully');
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;
      
      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=Strict;Secure'
      });
    };

    // Handle script loading errors
    script.onerror = (error) => {
      console.error('Failed to load Google Analytics:', error);
      // Remove the failed script element
      script.remove();
    };
  }

  private loadMarketingScripts(): void {
    // Example: Load Facebook Pixel, LinkedIn Insights, etc.
    console.log('Loading marketing scripts...');
    
    // Facebook Pixel example
    // if (!this.isScriptLoaded('facebook-pixel')) {
    //   // Facebook Pixel implementation
    // }
  }

  private loadFunctionalScripts(): void {
    // Example: Load chat widgets, preferences tools, etc.
    console.log('Loading functional scripts...');
    
    // Chat widget example
    // if (!this.isScriptLoaded('chat-widget')) {
    //   // Chat widget implementation
    // }
  }

  private isScriptLoaded(scriptId: string): boolean {
    return !!document.getElementById(scriptId);
  }

  private log(action: string, data?: any): void {
    // Only log in development mode
    if (typeof window !== 'undefined' && (window as any).location?.hostname === 'localhost') {
      console.log(`[CookieConsent] ${action}`, data);
    }
  }
} 